// ...existing code...
import React from "react";
import { motion } from "framer-motion";
import {
  FaCode,
  FaCloud,
  FaLaptopCode,
  FaShieldAlt,
  FaRocket,
  FaMobileAlt,
  FaUsersCog,
  FaProjectDiagram,
  FaCogs
} from "react-icons/fa";
import "./SoftwareDevelopment.css";
import { Link } from "react-router-dom";


const FEATURES = [
 {
    icon: <FaCode />,
    title: "Custom Application Development",
    text: "Tailored web, mobile, and enterprise applications designed to meet your business goals with high performance and scalability.",
  },
  {
    icon: <FaCloud />,
    title: "Cloud-Native Solutions",
    text: "Build and deploy applications on AWS, Azure, or GCP with optimized infrastructure and cost efficiency.",
  },
  {
    icon: <FaLaptopCode />,
    title: "Full-Stack Engineering",
    text: "End-to-end product engineering from frontend UI to backend microservices and database architecture.",
  },
  {
    icon: <FaMobileAlt />,
    title: "Mobile App Development",
    text: "Native and cross-platform apps that deliver seamless experiences across Android and iOS devices.",
  },
  {
    icon: <FaShieldAlt />,
    title: "Security & Compliance",
    text: "Secure SDLC, vulnerability testing, and best practices to ensure robust and compliant software systems.",
  },
  {
    icon: <FaUsersCog />,
    title: "Agile Development Teams",
    text: "Cross-functional teams that collaborate closely with your business to deliver rapid, measurable value.",
  },


];

const WHY = [
    {
    icon: <FaRocket />,
    title: "Faster Time to Market",
    text: "Streamlined DevOps and CI/CD pipelines to accelerate delivery and reduce downtime.",
  },
  {
    icon: <FaProjectDiagram />,
    title: "End-to-End Expertise",
    text: "From ideation to maintenance, our teams manage the full product lifecycle efficiently.",
  },
  {
    icon: <FaCogs />,
    title: "Scalable Architecture",
    text: "We design modular systems that grow seamlessly as your business evolves.",
  },

];

const Card = ({ icon, title, text, i }) => (
  <motion.article
    className="hc-card"
    initial={{ opacity: 0, y: 18, scale: 0.995 }}
    whileInView={{ opacity: 1, y: 0, scale: 1 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6, delay: i * 0.08 }}
    aria-labelledby={`hc-card-${i}-title`}
  >
    <div className="hc-icon" aria-hidden>
      {icon}
    </div>
    <div className="hc-card-body">
      <h3 id={`hc-card-${i}-title`}>{title}</h3>
      <p>{text}</p>
    </div>
  </motion.article>
);

const SoftwareDevelopment = () => {
  return (
    <main className="software-container">
      {/* Header without external image - decorative gradient + shapes via CSS */}
       <header className="software-header">
             <div className="header-content">
               <h1>SOFTWARE DEVELOPMENT</h1>
               <p className="subtitle">
              Crafting robust, scalable, and user-friendly software solutions tailored to your business goals—transforming ideas into high-performance digital products.  </p>
     
               <div className="header-ctas">
                  <Link
               to="/contact" // ✅ Navigate to Contact page
               className="btn-primary"
               aria-label="Contact Lyros"
             >
               Talk to our Expert team
             </Link>
               </div>
             </div>
     
             <svg className="header-decor" viewBox="0 0 800 200" preserveAspectRatio="none" aria-hidden="true">
               <defs>
                 <linearGradient id="gB" x1="0" x2="1">
                   <stop offset="0" stopColor="#0ea5e9" stopOpacity="0.14" />
                   <stop offset="1" stopColor="#6366f1" stopOpacity="0.06" />
                 </linearGradient>
               </defs>
               <path d="M0 140 C160 40 360 240 800 80 L800 200 L0 200 Z" fill="url(#gB)"></path>
             </svg>
           </header>
     

      <section className="container hc-intro">
        <motion.p
          className="section-desc"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
         We craft high-performance software and applications tailored to your unique business needs. From enterprise-grade systems to mobile apps, our development services ensure scalability, reliability, and security. Leveraging the latest in cloud, AI, and agile practices, we deliver digital experiences that drive growth and efficiency.  </motion.p>
      </section>

      <section className="container hc-features">
        <h2 className="section-title">Our Approach</h2>
        <div className="hc-cards">
          {FEATURES.map((f, idx) => (
            <Card key={idx} icon={f.icon} title={f.title} text={f.text} i={idx} />
          ))}
        </div>
      </section>

      <section className="container hc-examples">
        <h2 className="section-title"> Our Software Development Services</h2>
        <div className="hc-examples-grid">
          <motion.div
            className="example-card"
            initial={{ opacity: 0, x: -12 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h4>Custom Software Development</h4>
            <p>We design and develop bespoke software solutions that align with your operational goals, offering seamless integrations and optimized performance.</p>
          </motion.div>

          <motion.div
            className="example-card"
            initial={{ opacity: 0, x: 12 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.08 }}
          >
            <h4>Enterprise Web Applications</h4>
            <p>We create secure and scalable web apps to meet enterprise needs, enabling smooth internal processes, automation, and real-time analytics.</p>
          </motion.div>

          <motion.div
            className="example-card"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.16 }}
          >
            <h4>Mobile App Development</h4>
            <p>From iOS to Android, we build intuitive, responsive, and scalable mobile apps that enhance customer engagement and streamline business workflows.</p>
          </motion.div>
        </div>
      </section>

      <section className="container hc-why">
        <h2 className="section-title">Why Choose Lyros</h2>
        <div className="hc-why-grid">
          {WHY.map((w, i) => (
            <motion.div
              className="why-card"
              key={i}
              initial={{ opacity: 0, scale: 0.98 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
            >
              <div className="why-icon" aria-hidden>
                {w.icon}
              </div>
              <div className="why-content">
                <h4>{w.title}</h4>
                <p>{w.text}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>
    </main>
  );
};

export default SoftwareDevelopment ;
// ...existing code...